import { useState, useEffect, useCallback } from "react";
import { partnersOrderAggregator } from "../integrations/Partners/partnersOrderAggregator";
import store from "../store";
import { useCloud } from "./useCloud";
import { withSpan } from "../utils/sentry";

let partnerPollingID = "";
export const usePartnerOrders = () => {
  const { quickOrder } = useCloud();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all orders
  const fetchOrders = useCallback(async () => {
    return withSpan(
      {
        name: "Partner Orders Fetch All",
        op: "function",
        function: "fetchOrders",
        attributes: {
          component: "usePartnerOrders",
        },
      },
      async () => {
        setLoading(true);
        setError(null);
        try {
          const enabledPartners = store.restaurantInfo.partnerDetails
            .filter((partner) => partner.enabled)
            .map((partner) => partner.partnerName);
          const partners = Array.from(new Set(enabledPartners));
          
          const allOrders = await partnersOrderAggregator.getAllOrders(partners);
          setOrders(allOrders);
          store.partnersOrders = allOrders.filter((order) =>
            ["NEW", "PREPARING", "READY"].includes(order.orderStatus)
          );
          
          if (
            allOrders.length > 0 &&
            allOrders.some((order) => order.orderStatus === "NEW")
          ) {
            window.dispatchEvent(new Event("PARTNER_ORDER_RECEIVED"));
          } else {
            window.dispatchEvent(new Event("PARTNER_ORDER_EMPTY"));
          }

          for (const order of allOrders) {
            setTimeout(() => {
              trackOrder(order);
            }, 10);
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setTimeout(() => {
            setLoading(false);
          }, 2000);
        }
      }
    );
  }, []);

  // Get a single order
  const getOrder = useCallback(async (orderId, partnerId) => {
    return withSpan(
      {
        name: "Partner Orders Get Single",
        op: "function",
        function: "getOrder",
        attributes: {
          component: "usePartnerOrders",
          "order.id": orderId,
          "partner.id": partnerId,
        },
      },
      async () => {
        try {
          return await partnersOrderAggregator.getOrder(orderId, partnerId);
        } catch (err) {
          console.error(`Error fetching order ${orderId}:`, err);
          throw err;
        }
      }
    );
  }, []);

  // Helper functions
  const getOrdersByPartner = useCallback(
    (partnerId) => {
      return orders.filter((order) => order.partnerId === partnerId);
    },
    [orders]
  );

  const getOrdersByStatus = useCallback(
    (status) => {
      return orders.filter((order) => order.status === status);
    },
    [orders]
  );

  // Update outlet status
  const updateOutletStatus = useCallback(async (isOpen) => {
    return withSpan(
      {
        name: "Partner Orders Update Outlet Status",
        op: "function",
        function: "updateOutletStatus",
        attributes: {
          component: "usePartnerOrders",
          "outlet.is_open": isOpen,
        },
      },
      async () => {
        setLoading(true);
        try {
          await partnersOrderAggregator.updateOutletStatus(isOpen);
        } catch (err) {
          setError(err.message);
          throw err;
        } finally {
          setLoading(false);
        }
      }
    );
  }, []);

  const trackOrder = async (order) => {
    return withSpan(
      {
        name: "Partner Orders Track Order",
        op: "function",
        function: "trackOrder",
        attributes: {
          component: "usePartnerOrders",
          "order.id": order.orderId,
          "order.status": order.orderStatus,
          "partner.name": order.partnerName,
        },
      },
      async () => {
        let cachedOrder = localStorage.getItem(`partnerOrders`);
        cachedOrder = cachedOrder ? JSON.parse(cachedOrder) : {};

        if (order.orderStatusLevel > 1 && !cachedOrder[order.orderId]) {
          const prevLevel = cachedOrder[order.orderId] || 0;
          cachedOrder[order.orderId] = order.orderStatusLevel;
          localStorage.setItem(`partnerOrders`, JSON.stringify(cachedOrder));

          const transformedOrder = {
            order: {
              ...order,
              tableVPA: order.partnerName + "_" + order.partnerId,
              orderStatus: order.orderStatus === "CANCELLED" ? "cancelled" : "paid",
              onlineOrderCode: "",
              billStatus: order.orderStatus === "CANCELLED" ? "cancelled" : "paid",

              customerName: order.customer.name,
              customerPhone: order.customer.phone,
              customerAddress: order.customer.address.text,

              platformName: order.partnerName,
              orderId: "7b08f7b4-6b43-48e7-aeb8-4ed9ff935e67",
              items: order.items.reduce((acc, item) => {
                acc.push({ ...item, price: Number(item.price) });
                return acc;
              }, []),
            },
            payment: {
              orderId: "7b08f7b4-6b43-48e7-aeb8-4ed9ff935e67",
              tableVPA: order.partnerName + "_" + order.partnerId,
              paymentPending: false,
              txnLogs: [
                {
                  paymentMethodName: order.partnerName,
                  amount: Number(order.amount),
                },
              ],
            },
          };

          if (order.orderStatus == "CANCELLED") {
            transformedOrder.order.orderStatus = "cancelled";
            transformedOrder.order.billStatus = "cancelled";
            transformedOrder.order.billTxnLogs.total = 0;
            transformedOrder.order.bi = false;
            transformedOrder.payment.txnLogs = [
              {
                paymentMethodName: order.partnerName,
                amount: 0,
              },
            ];
          }
          delete transformedOrder.order.rawOrder;
          delete transformedOrder.order.delivery;
          delete transformedOrder.order.meta;

          try {
            await quickOrder(transformedOrder);
          } catch (err) {
            cachedOrder[order.orderId] = prevLevel;
            localStorage.setItem(`partnerOrders`, JSON.stringify(cachedOrder));
            console.error("Error tracking order:", err);
          }
        }
      }
    );
  };

  // Auto-refresh orders periodically
  useEffect(() => {
    if (partnerPollingID) {
      clearInterval(partnerPollingID);
    }
    fetchOrders();
    partnerPollingID = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
    return () => clearInterval(partnerPollingID);
  }, []);

  return {
    orders,
    setOrders,
    loading,
    error,
    fetchOrders,
    getOrder,
    getOrdersByPartner,
    getOrdersByStatus,
    updateOutletStatus,
    trackOrder,
  };
};
